import { ApolloQueryResult, useQuery } from '@apollo/client';
import { READ_CUSTOMER_SUBSCRIPTIONS } from 'graphql/queries/readCustomerSubscriptions';
import usePollSalesOrderLastRecurringPaymentStatus from 'hooks/usePollSalesOrderLastRecurringPaymentStatus';
import { useEffect, useMemo } from 'react';
import { SalesOrderReceipt } from 'screens/permit/SalesOrderReceiptClass';
import { useAuthStore } from 'stores/authStore';
import { Subscription } from 'stores/subscriptionStore';
import { Query } from 'types/generatedSchemaTypes';
import { handleSubscriptionPaymentFailedIndicator } from 'utils/indicatorUtils';
import { getSubscriptionData } from 'utils/permitUtils';

interface UseCustomerSubscriptionDataProps {
  aimoPlusSubscriber?: boolean;
}

interface UseCustomerSubscriptionDataReturn {
  customerSubscription: Subscription | undefined;
  loading: boolean;
  refetchCustomerSubscription: () => Promise<ApolloQueryResult<Query>>;
  isSubscriptionPaymentFailed: boolean;
}

/**
 * Data-only hook for fetching customer's active subscription.
 * Use this in AccountDetailsProvider to avoid hook dependency chain issues.
 * For components that need actions (purchase, update), use useSubscriptions instead.
 */
export const useCustomerSubscriptionData = ({
  aimoPlusSubscriber,
}: UseCustomerSubscriptionDataProps): UseCustomerSubscriptionDataReturn => {
  const token = useAuthStore((state) => state.accessToken);

  const {
    data: customerSubscriptions,
    loading,
    refetch: refetchCustomerSubscription,
  } = useQuery<Query>(READ_CUSTOMER_SUBSCRIPTIONS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !aimoPlusSubscriber || !token,
    errorPolicy: 'all',
  });

  const customerSubscriptionData =
    customerSubscriptions?.readCustomerSubscriptions?.[0] ?? null;

  const customerSubscriptionReceipt = useMemo(
    () => new SalesOrderReceipt(customerSubscriptionData),
    [customerSubscriptionData]
  );

  const customerSubscription = useMemo(
    () =>
      customerSubscriptionReceipt?.item
        ? getSubscriptionData(customerSubscriptionReceipt)
        : undefined,
    [customerSubscriptionReceipt]
  );

  // Poll for subscription payment status
  const { isFailed } = usePollSalesOrderLastRecurringPaymentStatus(
    { salesOrderUid: customerSubscription?.uid ?? '' },
    !customerSubscription?.uid
  );

  const isSubscriptionPaymentFailed = isFailed ?? false;

  // Update tab bar indicator when subscription payment status changes
  useEffect(() => {
    handleSubscriptionPaymentFailedIndicator(isSubscriptionPaymentFailed);
  }, [isSubscriptionPaymentFailed]);

  return {
    customerSubscription,
    loading,
    refetchCustomerSubscription,
    isSubscriptionPaymentFailed,
  };
};
